import gsap from "gsap";
import { Breakpoints } from "../../utils/breakpoints";
import { loadVidstack } from "../../utils/vidstack";
import { observeMediaElement } from "../../utils/mediaTelemetry";
import { loadBgVideo } from "../../utils/bgVideo";

const HERO_MARGIN_END = 22;

export default class HomepageHero {
  constructor(el) {
    this.el = el;
    this.media = el.querySelector("media-player, video");
    this.isPlayer = this.media?.tagName === "MEDIA-PLAYER";

    this.init();
    this.initVideo();
  }

  init() {
    // Product push stuck inside the hero (data-sticky-within): it used to follow
    // the shrinking margins on its own, now it's offset along with the clip.
    const push = this.el.querySelector(".c-modal-product-push[data-sticky-within]");

    Breakpoints.desktop(() => {
      // clip-path rather than margins + border-radius: same look without a
      // layout on every scrubbed frame. The push moves through the CSS
      // `translate` property (compositor-only, and NotifStack owns its `x`).
      gsap.fromTo(this.el, {
        clipPath: "inset(0px 0px 0px 0px round 0px)",
      }, {
        clipPath: `inset(0px ${HERO_MARGIN_END}px 0px ${HERO_MARGIN_END}px round 48px)`,
        ease: "none",
        onUpdate() {
          if (push) push.style.translate = `${-this.progress() * HERO_MARGIN_END}px 0px`;
        },
        scrollTrigger: {
          trigger: this.el,
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });

      // Leaving desktop reverts the tween, not our inline translate.
      return () => push && (push.style.translate = "");
    });
  }

  initVideo() {
    if (!this.media) return;

    this.stopTracking = observeMediaElement(this.media, {
      context: "hero",
      kind: "homepage",
      src: this.media.getAttribute("src") || this.media.dataset.src,
    });

    // Observes the video itself, not the section: it's display:none on mobile,
    // and an element without a box never intersects, so nothing (video,
    // Vidstack) is loaded there. Resizing up to desktop gives it a box, which
    // fires the observer then.
    this.onIntersect = this.onIntersect.bind(this);
    this.observer = new IntersectionObserver(this.onIntersect);
    this.observer.observe(this.media);
  }

  play() {
    if (!this.isPlayer) loadBgVideo(this.media);
    const p = this.media.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
  }

  onIntersect([entry]) {
    if (entry.isIntersecting) {
      if (this.isPlayer) (this.playerReady ??= loadVidstack()).then(() => this.play());
      else this.play();
    } else {
      this.media.pause?.();
    }
  }

  destroy() {
    this.observer?.disconnect();
    this.stopTracking?.();
  }
}
